import dayjs from 'dayjs';
import { useApplicationStore } from '@data';
import { formatDateToISO8601, getTimeZone } from '@utils';
import {
  getPatientProfileFixture,
  getUpcomingVisitFixture,
} from '@tests/fixtures';
import {
  PatientAccount,
  PatientGender,
  Provider,
  ReservedVisit,
  SelectedVisit,
  ServiceType,
  VisitMethod,
  VisitType,
} from '@types';
import getPatientCreditCardsFixture from '@tests/fixtures/getPatientCreditCardsFixture';

function getPatientAccountFixture(
  params?: Partial<PatientAccount>,
): PatientAccount {
  return {
    email: 'email@quick.md',
    address: {
      streetAddress1: '875 Nimes Road',
      streetAddress2: '',
      zip: '90077',
      city: 'Bel Air',
      stateCode: 'CA',
      countryCode: 'US',
    },
    birthDate: '1960-01-01',
    firstName: 'John',
    lastName: 'Doe',
    gender: PatientGender.MALE,
    ...params,
  };
}

function getProviderFixture(params?: Partial<Provider>): Provider {
  return {
    firstName: 'Cameron',
    id: 'provider-1',
    imageUrl: '',
    lastName: 'Poe',
    prefix: 'Dr.',
    suffix: 'MD',
    ...params,
  };
}

export function getReservedVisitFixture(
  params?: Partial<ReservedVisit>,
): ReservedVisit {
  const expireDateTime = formatDateToISO8601(
    dayjs().add(10, 'minute').toDate(),
  );

  return {
    allowedMethods: [VisitMethod.CALL, VisitMethod.VIDEO],
    reservationId: 'reservation-1',
    expireDateTime,
    price: 615,
    consentForms: [
      {
        name: 'Telehealth Consent Form',
        url: 'https://example.com/consent1',
        individualConsentRequired: false,
      },
      {
        name: 'Privacy Policy',
        url: 'https://example.com/privacy',
        individualConsentRequired: false,
      },
    ],
    isFollowupVisitIncluded: true,
    isReferralAllowed: false,
    subscriptionBasedPrice: null,
    visitCreditAmount: 0,
    ...params,
  };
}

export function getSelectedVisitFixture(
  provider: Provider,
  params?: Partial<SelectedVisit>,
): SelectedVisit {
  return {
    date: dayjs().format('YYYY-MM-DD'),
    provider,
    time: '11:00',
    timeZone: getTimeZone(),
    visitId: 'visit-1',
    visitTypeId: 'visit-type-1',
    ...params,
  };
}

function getServiceTypeFixture(
  visitTypes: VisitType[],
  params?: Partial<ServiceType>,
): ServiceType {
  return {
    abbreviation: 'mht',
    category: 'MenopauseHormoneTherapy',
    description:
      'Medication-assisted treatment (MAT) for opioid addiction with Suboxone/buprenorphine',
    imageUrl:
      'https://quickmdqmd2storage.blob.core.windows.net/qa-images/service-types/urgent-care.png',
    name: 'Urgent Care - Suboxone',
    serviceTypeId: 'service-type-1',
    shortDescription: '$49 Consultation',
    isInPersonVisitRequired: false,
    visitTypes,
    ...params,
  };
}

function getVisitTypeFixture(params?: Partial<VisitType>): VisitType {
  return {
    visitTypeId: 'visit-type-1',
    name: 'Recurring Visit',
    description: 'Recurring Visit',
    price: 70.94,
    isInPerson: false,
    isDrugScreen: false,
    isReferralAllowed: true,
    referralCreditAmount: 100,
    ...params,
  };
}

function seedAuthData() {
  const { auth } = useApplicationStore.getState();
  const { setAuthData } = auth;

  setAuthData({
    accessToken: 'access-token-1',
    sessionId: 'session-id-1',
  });
}

function seedPatientAccount() {
  const { mergePatientAccount } = useApplicationStore.getState();

  const patientAccount = getPatientAccountFixture();

  mergePatientAccount(patientAccount);
}
function seedPatientCreditCards() {
  const { patient } = useApplicationStore.getState();
  const { setPatientCreditCards } = patient;
  const patientCreditCards = getPatientCreditCardsFixture();

  setPatientCreditCards(patientCreditCards);
}

function seedPatientProfile() {
  const { patient } = useApplicationStore.getState();
  const { setPatientProfile } = patient;
  const patientProfile = getPatientProfileFixture();

  setPatientProfile(patientProfile);
}

function seedReservedVisit() {
  const { setReservedVisit } = useApplicationStore.getState();

  const reservedVisit = getReservedVisitFixture();

  setReservedVisit(reservedVisit);
}

function seedServiceType() {
  const { setServiceTypes } = useApplicationStore.getState();

  const visitType = getVisitTypeFixture();
  const serviceType = getServiceTypeFixture([visitType]);

  setServiceTypes([serviceType]);
}

function seedUpcomingVisit() {
  const { setUpcomingVisits } = useApplicationStore.getState().patient;
  const dateTime = dayjs().add(30, 'second').toISOString();
  const upcomingVisit = getUpcomingVisitFixture({
    dateTime,
  });

  setUpcomingVisits([upcomingVisit]);
}

function seedVisit() {
  const { setVisit } = useApplicationStore.getState();

  const provider = getProviderFixture();
  const selectedVisit = getSelectedVisitFixture(provider);

  setVisit(selectedVisit);
}

export function seedStore() {
  seedAuthData();
  seedPatientAccount();
  seedPatientCreditCards();
  seedPatientProfile();
  seedReservedVisit();
  seedServiceType();
  seedUpcomingVisit();
  seedVisit();
}
